'use client';

import { PrismicNextImage } from '@prismicio/next';
import { BlogDocument } from '../../../prismicio-types';
import { asText, ImageField } from '@prismicio/client';
import Link from 'next/link';
import { getBlogPublishedDate } from '@/utils';

interface CategoryCardProps {
  post: BlogDocument;
  category: string;
}

interface AuthorData {
  name?: string;
  avatar?: ImageField;
}

interface AuthorWithData {
  data?: AuthorData;
}

export default function CategoryCard({ post, category }: CategoryCardProps) {
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const renderDateAndDuration = () => {
    const publishedDate = getBlogPublishedDate(post);
    const parsedDate = publishedDate ? formatDate(publishedDate) : '';
    const parsedDuration = post.data.read_duration
      ? `• ${post.data.read_duration} min read`
      : '';
    return (
      <div className="text-sm text-white">
        {parsedDate} {parsedDuration}
      </div>
    );
  };

  // Type guard for author data
  const author = post.data.author as unknown as AuthorWithData;
  const authorData = author?.data;

  return (
    <div
      className="flex flex-col rounded-[20px] overflow-hidden text-start text-white h-full"
      style={{
        background:
          'linear-gradient(to right, rgba(255,255,255,0.15), rgba(3,191,158,0.15), rgba(255,255,255,0.15))',
      }}
    >
      {post.data.image?.url && (
        <div className="relative h-48">
          <PrismicNextImage
            field={post.data.image}
            className="h-full w-full object-cover rounded-t-[20px]"
            loading="lazy"
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 400px"
            imgixParams={{ fit: 'crop', ar: '16:9', q: 75 }}
          />
          {/* Abstract overlay - simplified for Tailwind */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
          <div className="absolute top-4 right-4 text-white"></div>
        </div>
      )}
      <div className="p-6 flex flex-col justify-between h-full">
        <div>
          <Link href={`/resources-center/${post.uid}`}>
            <h3 className="text-xl font-bold text-white mb-2">
              {asText(post.data.card_title)}
            </h3>
          </Link>
        </div>
        <div className="flex flex-col gap-4">
          <p className="text-[#05F273] text-sm font-semibold uppercase">
            <span className="text-xs text-[#05F273] font-semibold tracking-wider no-underline">
              {category.toUpperCase()}
            </span>
          </p>
          <div className="flex items-center space-x-3">
            <div className="flex gap-4">
              {authorData?.avatar ? (
                <PrismicNextImage
                  field={authorData.avatar}
                  className="rounded-[6px] h-14 w-14 object-cover"
                  height={56}
                  width={56}
                  loading="lazy"
                  imgixParams={{ fit: 'crop', ar: '1:1', q: 75 }}
                />
              ) : (
                <div className="rounded-[6px] h-14 w-14 bg-gray-600 flex items-center justify-center object-cover">
                  <span className="text-sm font-medium text-white">D</span>
                </div>
              )}
              <div className="flex flex-col justify-end text-sm">
                <div className="font-semibold mb-2 text-white">
                  {authorData?.name || 'Devsu'}
                </div>
                {renderDateAndDuration()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
